import React, { useEffect, useState } from 'react';
import { appendVariant, buildChatAssetUrl } from '../chat/composer/chatAssetUtils';
import '../../../css/common/avatar/PersonAvatar.css';

const API_URL = process.env.REACT_APP_API_URL || '';
const ADMIN_PROFILE_IMAGE_PATH = (
  process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH || '/api/images/admin_profiles'
).replace(/\/$/, '');
const ARTIST_PROFILE_IMAGE_PATH = (
  process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH || '/api/images/artist_profiles'
).replace(/\/$/, '');

const ROLE_ALIASES = {
  admin: 'admin',
  administrator: 'admin',
  staff: 'admin',
  artist: 'artist',
};

const GENDER_ALIASES = {
  male: 'male',
  man: 'male',
  m: 'male',
  男性: 'male',
  男: 'male',
  female: 'female',
  woman: 'female',
  f: 'female',
  女性: 'female',
  女: 'female',
  secret: 'unknown',
  private: 'unknown',
  hidden: 'unknown',
  unknown: 'unknown',
  unset: 'unknown',
  未設定: 'unknown',
};

const EMPTY_IMAGE_VALUES = new Set(['', 'null', 'undefined', 'none']);

export function normalizeAvatarImageValue(value) {
  const text = String(value ?? '').trim();
  return EMPTY_IMAGE_VALUES.has(text.toLowerCase()) ? '' : text;
}

export function normalizePersonRole(role) {
  const key = String(role || '').trim().toLowerCase();
  return ROLE_ALIASES[key] || 'admin';
}

export function normalizePersonGender(gender) {
  const key = String(gender || '').trim().toLowerCase();
  return GENDER_ALIASES[key] || 'unknown';
}

export function getPersonDisplayName(person = {}, fallback = 'ユーザー') {
  return (
    person?.displayName ||
    person?.display_name ||
    person?.fullName ||
    person?.full_name ||
    person?.name ||
    person?.name_en ||
    person?.username ||
    person?.email ||
    fallback
  );
}

export function getPersonInitial(name, fallback = '?') {
  const label = String(name || '').trim();
  return label ? label.charAt(0).toUpperCase() : fallback;
}

export function getPersonProfilePicture(person = {}) {
  return normalizeAvatarImageValue(
    person?.profilePicture ||
    person?.profile_picture ||
    person?.profile_picture_url ||
    person?.profilePictureUrl ||
    person?.picture ||
    person?.avatarUrl ||
    person?.avatar_url ||
    ''
  );
}

export function getPersonGender(person = {}) {
  return (
    person?.gender ||
    person?.artistGender ||
    person?.artist_gender ||
    person?.profileGender ||
    person?.profile_gender ||
    person?.artist?.gender ||
    ''
  );
}

export function buildPersonAvatarUrl({
  role = 'admin',
  profilePicture = '',
  imageUrl = '',
  variant = 'icon',
  apiUrl = API_URL,
  root = '',
} = {}) {
  const directImageUrl = normalizeAvatarImageValue(imageUrl);
  const file = normalizeAvatarImageValue(profilePicture);
  const source = directImageUrl || file;
  if (!source) return '';

  const normalizedRole = normalizePersonRole(role);
  const imageRoot = root || (normalizedRole === 'artist' ? ARTIST_PROFILE_IMAGE_PATH : ADMIN_PROFILE_IMAGE_PATH);
  const url = directImageUrl || buildChatAssetUrl({ apiUrl, root: imageRoot, file });
  return variant ? appendVariant(url, variant) : url;
}

export function normalizeAvatarSizeValue(size) {
  if (size === null || size === undefined || size === '') return '';
  if (typeof size === 'number') return Number.isFinite(size) ? `${size}px` : '';
  const text = String(size).trim();
  if (!text) return '';
  return /^\d+(\.\d+)?$/.test(text) ? `${text}px` : text;
}

export function PersonAvatar({
  person,
  role,
  type,
  gender,
  name,
  profilePicture,
  imageUrl,
  variant = 'icon',
  fallback = '?',
  className = '',
  imageClassName = '',
  initialClassName = '',
  title = '',
  size,
  style: customStyle,
  onClick,
  ariaHidden = false,
  unassigned = false,
  children,
}) {
  const resolvedRole = normalizePersonRole(role || type || person?.role || person?.type);
  const resolvedGender = normalizePersonGender(gender ?? getPersonGender(person));
  const resolvedName = String(name || getPersonDisplayName(person, fallback)).trim() || fallback;
  const resolvedProfilePicture = profilePicture ?? getPersonProfilePicture(person);
  const avatarUrl = buildPersonAvatarUrl({
    role: resolvedRole,
    profilePicture: resolvedProfilePicture,
    imageUrl,
    variant,
  });
  const [failedImageUrl, setFailedImageUrl] = useState('');
  useEffect(() => {
    setFailedImageUrl('');
  }, [avatarUrl]);
  const visibleAvatarUrl = avatarUrl && failedImageUrl !== avatarUrl ? avatarUrl : '';
  const avatarSize = normalizeAvatarSizeValue(size);
  const numericAvatarSize = Number(size);
  const avatarIconSize = Number.isFinite(numericAvatarSize) && numericAvatarSize > 0
    ? `${Math.max(10, Math.round((numericAvatarSize * 0.6) / 2) * 2)}px`
    : '';
  const style = {
    ...(avatarSize ? { '--person-avatar-size': avatarSize } : {}),
    ...(avatarIconSize ? { '--person-avatar-icon-size': avatarIconSize } : {}),
    ...(customStyle || {}),
  };
  const classNames = [
    'person-avatar',
    `is-${resolvedRole}`,
    resolvedRole === 'artist' && !visibleAvatarUrl ? `is-gender-${resolvedGender}` : '',
    visibleAvatarUrl ? 'has-image' : '',
    unassigned && !visibleAvatarUrl ? 'is-unassigned' : '',
    onClick ? 'is-clickable' : '',
    className,
  ].filter(Boolean).join(' ');
  const content = visibleAvatarUrl ? (
    <img
      className={imageClassName || undefined}
      src={visibleAvatarUrl}
      alt={resolvedName}
      loading="lazy"
      decoding="async"
      onError={() => setFailedImageUrl(visibleAvatarUrl)}
    />
  ) : unassigned ? (
    <i className="fa-solid fa-user person-avatar__unassigned-icon" aria-hidden="true" />
  ) : (
    <span className={['person-avatar__initial', initialClassName].filter(Boolean).join(' ')}>
      {getPersonInitial(resolvedName, fallback)}
    </span>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={classNames}
        title={title || resolvedName}
        style={style}
        onClick={onClick}
        aria-label={resolvedName}
      >
        {content}
        {children}
      </button>
    );
  }

  return (
    <span
      className={classNames}
      title={title || resolvedName}
      style={style}
      aria-hidden={ariaHidden || undefined}
    >
      {content}
      {children}
    </span>
  );
}

export function PersonAvatarLabel({
  person,
  role,
  type,
  gender,
  name,
  profilePicture,
  imageUrl,
  variant = 'icon',
  fallback = '?',
  label,
  subLabel = '',
  className = '',
  avatarClassName = '',
  textClassName = '',
  nameClassName = '',
  subLabelClassName = '',
  size,
  onClick,
  children,
}) {
  const resolvedName = String(label || name || getPersonDisplayName(person, fallback)).trim() || fallback;
  const content = (
    <>
      <PersonAvatar
        person={person}
        role={role}
        type={type}
        gender={gender}
        name={resolvedName}
        profilePicture={profilePicture}
        imageUrl={imageUrl}
        variant={variant}
        fallback={fallback}
        className={avatarClassName}
        size={size}
      />
      <span className={['person-avatar-label__text', textClassName].filter(Boolean).join(' ')}>
        <span className={['person-avatar-label__name', nameClassName].filter(Boolean).join(' ')}>
          {children || resolvedName}
        </span>
        {subLabel ? (
          <span className={['person-avatar-label__sub', subLabelClassName].filter(Boolean).join(' ')}>
            {subLabel}
          </span>
        ) : null}
      </span>
    </>
  );

  if (onClick) {
    return (
      <button type="button" className={['person-avatar-label', className].filter(Boolean).join(' ')} onClick={onClick}>
        {content}
      </button>
    );
  }

  return (
    <span className={['person-avatar-label', className].filter(Boolean).join(' ')}>
      {content}
    </span>
  );
}

export function ChatPersonAvatarLabel({
  person,
  role,
  type,
  gender,
  displayName,
  nickname,
  useNickname = false,
  onProfileOpen,
  ...props
}) {
  const primaryName = useNickname && nickname ? nickname : displayName;
  return (
    <PersonAvatarLabel
      {...props}
      person={person}
      role={role}
      type={type}
      gender={gender}
      name={primaryName}
      label={primaryName}
      onClick={onProfileOpen}
    />
  );
}

export default PersonAvatar;
